import { useVirtualizer, Virtualizer } from '@tanstack/react-virtual'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import AutoSizer from 'react-virtualized-auto-sizer'
import {
  getInactivePagesEventIds,
  getPage1EventIds,
  getPage2EventIds,
  ListKey
} from 'astra-core/containers/EventsProvider'
import {
  EventsListItems,
  EventsListItemType
} from 'astra-core/utils/eventsList'

import { useAppSelector } from 'store'
import { usePrevious } from 'hooks/usePrevious'
import { LoaderSpinner } from 'shared/ui/LoaderSpinner'
import { OutcomesFetchType } from 'entities/outcomes/model/types'
import { ETableHeadVariant, TableBetsHead } from 'widgets/event-row-head'

import { EventRowHoc as EventRow } from './EventRow'
import {
  ListInner,
  ListOuter,
  ListWrapper
} from './VirtualizedEventsList.styled'
import { SportHeader } from './SportHeader'
import { DateHeader } from './DateHeader'

interface Props {
  listItems: EventsListItems
  pageNumbersOfListItems: Array<number>
  listKey: ListKey
  hasNextPage: boolean
  isNextPageLoading: boolean
  loadNextPage: () => void
  scrollUp: (pageNumber: number) => void
  view?: 'calendar' | 'live' | 'line'
  outcomesFetchType?: OutcomesFetchType
  height: number
  width: number
}

export const VirtualizedEventsList = ({
  listItems,
  pageNumbersOfListItems,
  outcomesFetchType,
  listKey,
  hasNextPage,
  isNextPageLoading,
  loadNextPage,
  scrollUp,
  view,
  height,
  width
}: Props) => {
  const [visiblePageIndex, setVisiblePageIndex] = useState(0)
  const prevVisiblePageIndex = usePrevious(visiblePageIndex)

  useEffect(() => {
    if (visiblePageIndex < prevVisiblePageIndex) {
      scrollUp(visiblePageIndex)
    }
  }, [prevVisiblePageIndex, visiblePageIndex, scrollUp])

  const page1EventIds = useAppSelector((state) =>
    getPage1EventIds(state, listKey)
  )
  const page2EventIds = useAppSelector((state) =>
    getPage2EventIds(state, listKey)
  )
  const inactivePagesEventIds = useAppSelector((state) =>
    getInactivePagesEventIds(state, listKey)
  )
  const inactivePagesEventIdsSet = useMemo(() => {
    return new Set(inactivePagesEventIds)
  }, [inactivePagesEventIds])
  const parentRef = React.useRef<HTMLDivElement>(null)

  const onChange = useCallback(
    (instance: Virtualizer<HTMLDivElement, Element>) => {
      if (!instance.isScrolling && instance.range?.startIndex !== undefined) {
        setVisiblePageIndex(pageNumbersOfListItems[instance.range.startIndex])
      }
    },
    [setVisiblePageIndex, pageNumbersOfListItems]
  )

  const virtualizer = useVirtualizer({
    count: hasNextPage ? listItems.length + 1 : listItems.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 40,
    overscan: 2,
    enabled: true,
    onChange
  })

  const items = virtualizer.getVirtualItems()

  React.useEffect(() => {
    const lastItem = items[items.length - 1]

    if (!lastItem) {
      return
    }

    const shouldLoadNextPage =
      lastItem.index >= listItems.length - 1 &&
      hasNextPage &&
      !isNextPageLoading
    if (shouldLoadNextPage) {
      loadNextPage()
    }
  }, [hasNextPage, loadNextPage, listItems.length, isNextPageLoading, items])

  const totalHeight = virtualizer.getTotalSize()

  return (
    <ListWrapper
      style={{
        height,
        width,
        overflowY: 'auto'
      }}
      ref={parentRef}
    >
      <ListOuter style={{ height: totalHeight }}>
        <ListInner
          style={{
            transform: `translateY(${items[0]?.start ?? 0}px)`
          }}
        >
          {items.map((virtualRow) => {
            const isLoaderRow = virtualRow.index > listItems.length - 1
            let content
            if (isLoaderRow) {
              if (hasNextPage) {
                content = <LoaderSpinner />
              } else {
                content = null
              }
            } else {
              const listItem = listItems[virtualRow.index]
              switch (listItem.type) {
                case EventsListItemType.Event: {
                  const eventId = listItem.event.id
                  const isActive = !inactivePagesEventIdsSet.has(eventId)
                  content = (
                    <EventRow
                      event={listItem.event}
                      inactivePagesEventIds={inactivePagesEventIds}
                      key={`${eventId}-${isActive}`}
                      outcomesFetchType={outcomesFetchType}
                      page1EventIds={page1EventIds}
                      page2EventIds={page2EventIds}
                    />
                  )
                  break
                }
                case EventsListItemType.Tournament: {
                  content = (
                    <TableBetsHead
                      isLink={view === 'line'}
                      tournament={listItem.tournament}
                      variant={ETableHeadVariant.TWO}
                    />
                  )
                  break
                }
                case EventsListItemType.Sport: {
                  content = <SportHeader sportId={listItem.sportId} />
                  break
                }
                case EventsListItemType.Date: {
                  content = <DateHeader date={listItem.date} view={view} />
                  break
                }
                default:
                  break
              }
            }
            return (
              <div
                data-index={virtualRow.index}
                key={virtualRow.key.toString()}
                ref={virtualizer.measureElement}
              >
                {content}
              </div>
            )
          })}
        </ListInner>
      </ListOuter>
    </ListWrapper>
  )
}

const withAutoSizer = (Component) => {
  return (props: Omit<Props, 'width' | 'height'>) => {
    return (
      <div style={{ height: '100%' }}>
        <AutoSizer>
          {({ height, width }) => {
            return <Component {...props} height={height} width={width} />
          }}
        </AutoSizer>
      </div>
    )
  }
}

export const VirtualizedEventsListWithAutoSize = withAutoSizer(
  VirtualizedEventsList
)
